import { createFileRoute } from "@tanstack/react-router";
import { getLocalizedMeta } from "@/lib/utils/seo";
import { Mail, Phone } from "lucide-react";
import { LegalPage } from "@/components/layout/Page";
import { COMPANY } from "@/lib/company";
import { useI18n } from "@/context/I18nContext";
import { getCurrentLocale } from "@/lib/utils/locale";

/**
 * Bilingual legal page.
 *
 * Legal copy is kept as JSX here rather than in the locale JSON: it is
 * long-form structured prose (headings, lists, inline emphasis and links), and
 * flattening it into key/value strings would both mangle the structure and
 * make review by a lawyer impractical. Turkish is the authoritative version —
 * VARS is an İzmir company and KVKK is Turkish law — with English served to
 * every other locale as the working translation for international buyers.
 *
 * Statutory identifiers (tax ID, MERSİS, registered address) are identical in
 * both versions and match the other three legal routes.
 */
export const Route = createFileRoute("/privacy")({
  // Was a hand-rolled tr/en conditional with no og:/twitter: tags, so the
  // other seven locales read English and every social card fell through to
  // the homepage's. SEO_COPY carries all nine locales for this page now.
  head: () => ({ meta: getLocalizedMeta("privacy") }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const { lang } = useI18n();
  return lang === "tr" ? <PrivacyTr /> : <PrivacyEn />;
}

function CorporateDetailsTr() {
  return (
    <div className="mt-8 rounded-2xl bg-surface-alt p-6 border border-border space-y-2 text-xs">
      <h3 className="font-display text-sm font-bold text-navy uppercase tracking-wider">
        Veri Sorumlusu Kurumsal Bilgileri
      </h3>
      <p className="font-bold text-navy">{COMPANY.legalName}</p>
      <p>{COMPANY.address.full}</p>
      <p>Vergi Kimlik No: 9240533729 | MERSİS: 0924053372900001</p>
      <p className="inline-flex flex-wrap items-center gap-1.5">
        <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> E-posta:{" "}
        <a href="mailto:privacy@varsco.com" className="text-primary font-bold">
          privacy@varsco.com
        </a>{" "}
        | <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Tel: +90 232 290 57 56
      </p>
    </div>
  );
}

function PrivacyTr() {
  return (
    <LegalPage title="Gizlilik Politikası">
      <p className="text-xs text-muted-foreground font-semibold">Yürürlük Tarihi: 01/08/2025</p>
      <p>
        <strong>{COMPANY.legalName}</strong> (“Şirket”, “biz” veya “VARS”) gizliliğinize ve veri
        güvenliğinize önem vermektedir. İşbu Gizlilik Politikası;{" "}
        <a href="https://varsco.com" className="text-primary font-bold hover:underline">
          varsco.com
        </a>{" "}
        web sitemizi ziyaret ettiğinizde, B2B hizmetlerimizden yararlandığınızda veya ürünlerimizi
        satın aldığınızda kişisel verilerinizi{" "}
        <strong>6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK)</strong>,{" "}
        <strong>Genel Veri Koruma Tüzüğü (GDPR)</strong> ve ilgili uluslararası ticari mevzuata
        uygun olarak nasıl topladığımızı, kullandığımızı, sakladığımızı ve koruduğumuzu
        açıklamaktadır.
      </p>

      <h2>1. Topladığımız Bilgiler</h2>
      <p>
        <strong>A. Kişisel Veriler</strong>
      </p>
      <ul>
        <li>Ad soyad ve kurumsal unvan</li>
        <li>Kurumsal e-posta adresi ile telefon / WhatsApp numaraları</li>
        <li>Şirket unvanı, vergi dairesi ve vergi kimlik numarası (B2B faturalandırma için)</li>
        <li>Soğuk zincir ve kuru yük teslimatı için sevkiyat ve fatura adresleri</li>
        <li>
          Ödeme işlem verileri (<strong>iyzico PCI-DSS sertifikalı altyapısı</strong> üzerinden
          güvenli şekilde işlenir, sunucularımızda saklanmaz)
        </li>
      </ul>
      <p>
        <strong>B. Kişisel Olmayan ve Teknik Veriler</strong>
      </p>
      <ul>
        <li>IP adresi, tarayıcı türü, işletim sistemi</li>
        <li>Cihaz bilgileri ve oturum telemetrisi</li>
        <li>Çerezler ve site kullanım verileri (ziyaret edilen sayfalar, yönlendiren kaynaklar)</li>
      </ul>

      <h2>2. Bilgilerinizi Nasıl Kullanıyoruz?</h2>
      <ul>
        <li>
          B2B satın alma siparişlerini ve teklif taleplerini işlemek, doğrulamak ve sevk etmek
        </li>
        <li>Müşteri hizmetleri, teknik kuluçkahane desteği ve SLA takibi sağlamak</li>
        <li>
          Türk vergi ve ticaret mevzuatı kapsamında yasal E-fatura / E-arşiv belgeleri düzenlemek
        </li>
        <li>Gümrük, veteriner ihracat sertifikaları ve düzenleyici gerekliliklere uyum sağlamak</li>
        <li>Teknik güncellemeler ve tanıtım bültenleri göndermek (açık rızanıza bağlı olarak)</li>
      </ul>

      <h2>3. Bilgilerin Paylaşılması</h2>
      <p>Kişisel verilerinizi yalnızca ilgili mevzuata tam uygunlukla paylaşırız:</p>
      <ul>
        <li>Sipariş ifası için soğuk zincir lojistiği ve hava kargo taşıyıcılarıyla</li>
        <li>
          PCI-DSS güvenli kart işlemleri için ödeme hizmeti sağlayıcılarıyla (
          <strong>iyzico</strong>)
        </li>
        <li>Yetkili bölgesel satış birimleri ve dağıtım iş ortaklarıyla</li>
        <li>
          Mevzuatın zorunlu kıldığı hâllerde vergi daireleri, veteriner müdürlükleri ve yasal
          mercilerle
        </li>
      </ul>
      <p className="font-bold text-navy">
        Kişisel verilerinizi ÜÇÜNCÜ TARAFLARA HİÇBİR ZAMAN satmaz, kiralamaz veya devretmeyiz.
      </p>

      <h2>4. Veri Güvenliği</h2>
      <ul>
        <li>Tüm web iletimi için 256 bit SSL şifreleme</li>
        <li>Yetkisiz erişime karşı sıkı idari ve teknik tedbirler</li>
        <li>
          Çevrimiçi ödeme işlemleri <strong>PCI-DSS Seviye 1 sertifikalı iyzico altyapısı</strong>{" "}
          üzerinden işlenir
        </li>
        <li>
          Veriler <strong>6698 sayılı KVKK</strong> ve GDPR saklama standartlarına uygun olarak
          muhafaza edilir
        </li>
      </ul>

      <h2>5. Çerezler ve Analitik</h2>
      <p>
        Yalnızca sitenin çalışması için gerekli olanları saklıyoruz — dil tercihiniz, oturumunuz ve
        sepetiniz. Bunlar rıza gerektirmez ve siteyi kullandığınız sürece reddedilemez.
      </p>
      <p>
        <strong>Ziyaretçi istatistikleri.</strong> Ziyaretleri kendi sunucumuzda,{" "}
        <strong>stats.varsco.com</strong> adresinde, kendi barındırdığımız bir yazılımla (Umami)
        sayıyoruz; bu yazılım çerez yerleştirmez ve cihazınızda hiçbir şey saklamaz. Açtığınız
        sayfayı, sizi yönlendiren siteyi, bağlantınızın çözümlendiği ülkeyi ve tarayıcı ile cihaz
        türünüzü kaydeder. Ziyaretler, her gün yeniden üretilen tek yönlü bir kodla tek bir oturumda
        gruplanır; bu nedenle dünkü bir ziyaret bugünkü bir ziyaretle eşleştirilemez ve başka hiçbir
        web sitesinde tanınmazsınız. Adınız, e-posta adresiniz veya bir forma yazdığınız hiçbir
        bilgi buraya ulaşmaz. Cihazınızda hiçbir şey saklanmadığı ve veriler kendi sistemlerimizin
        dışına çıkmadığı için bu sayım her ziyaretçi için çalışır ve aşağıdaki tercihin kapsamında
        değildir.
      </p>
      <p>
        Analitik bundan ayrıdır ve yalnızca izin vermeniz hâlinde çalışır. Siz seçim yapmadan önce
        hiçbir veri toplanmaz; varsayılan olan reddetmektir. Etkinleştirildiğinde ziyaret ettiğiniz
        sayfaları, sitenin görüntülendiği dili, bağlantınızın çözümlendiği ülkeyi ve bir
        ziyaretçinin sayfalarını tek bir ziyaret olarak saymamızı sağlayan rastgele oluşturulmuş bir
        tanımlayıcıyı kaydediyoruz. Bu tanımlayıcı adınıza veya e-posta adresinize bağlı değildir ve
        sizi başka web sitelerinde takip etmiyoruz.
      </p>
      <p>
        Tercihiniz çerezde değil tarayıcınızda saklanır; bu nedenle tarayıcının çerez ayarları bu
        tercihi kontrol etmez. Dilediğiniz zaman değiştirmek veya geri çekmek için herhangi bir
        sayfanın altbilgisindeki <strong>Çerez tercihleri</strong> bağlantısını kullanın. Geri çekme
        işlemi derhal yürürlüğe girer ve yukarıda açıklanan tanımlayıcıyı da siler.
      </p>
      <p>
        Analitik kayıtları 60 günlük hareketsizliğin ardından otomatik olarak silinir. Analitik
        açıkken bize bir talep gönderirseniz, görüntülediğiniz sayfalar o talebe eklenir ve talep
        kaydının bir parçası olarak, talebi sakladığımız süre boyunca saklanır.
      </p>

      <h2>6. Haklarınız</h2>
      <p>KVKK ve GDPR kapsamında aşağıdaki haklara sahipsiniz:</p>
      <ul>
        <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme ve bunlara erişim talep etme</li>
        <li>Yanlış veya eksik kişisel verilerin düzeltilmesini talep etme</li>
        <li>
          Verilerin silinmesini veya yok edilmesini talep etme (yasal vergi saklama yükümlülükleri
          saklı kalmak kaydıyla)
        </li>
        <li>Otomatik işlemeye itiraz etme veya pazarlama rızasını geri çekme</li>
      </ul>
      <p>
        Haklarınızı kullanmak için veri sorumlumuza{" "}
        <a href="mailto:privacy@varsco.com" className="text-primary font-bold hover:underline">
          privacy@varsco.com
        </a>{" "}
        adresinden e-posta gönderebilirsiniz.
      </p>

      <h2>7. Güncellemeler</h2>
      <p>
        İşbu politika dönemsel olarak güncellenebilir. En güncel sürüm, güncellenmiş yürürlük tarihi
        ile birlikte her zaman portalımızda erişilebilir olacaktır.
      </p>

      <CorporateDetailsTr />
    </LegalPage>
  );
}

function PrivacyEn() {
  return (
    <LegalPage title="Privacy Policy">
      <p className="text-xs text-muted-foreground font-semibold">Effective Date: 01/08/2025</p>
      <p>
        <strong>{COMPANY.legalName}</strong> (“Company”, “we”, “our” or “VARS”) values your privacy
        and data security. This Privacy Policy explains how we collect, use, store, and protect your
        personal data in compliance with the{" "}
        <strong>Turkish Personal Data Protection Law (KVKK - Law No. 6698)</strong>, the{" "}
        <strong>General Data Protection Regulation (GDPR)</strong>, and applicable international
        commercial regulations when you visit our website{" "}
        <a href="https://varsco.com" className="text-primary font-bold hover:underline">
          varsco.com
        </a>
        , use our B2B services, or purchase our products.
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        <strong>A. Personal Data</strong>
      </p>
      <ul>
        <li>Full name and corporate position</li>
        <li>Corporate email address and telephone / WhatsApp numbers</li>
        <li>Company name, Tax Office, Tax ID (for B2B invoicing)</li>
        <li>Shipping and billing addresses for cold-chain and dry cargo delivery</li>
        <li>
          Payment transaction data (processed securely via{" "}
          <strong>iyzico PCI-DSS certified infrastructure</strong>, never stored on our servers)
        </li>
      </ul>
      <p>
        <strong>B. Non-Personal & Technical Data</strong>
      </p>
      <ul>
        <li>IP address, browser type, operating system</li>
        <li>Device information and session telemetry</li>
        <li>Cookies and site usage data (pages visited, referral sources)</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To process, verify, and dispatch B2B purchase orders and quote requests</li>
        <li>To provide customer service, technical hatchery support, and SLA tracking</li>
        <li>To issue legal E-fatura / E-arşiv invoices under Turkish tax and commerce laws</li>
        <li>To comply with customs, veterinary export certificates, and regulatory requirements</li>
        <li>To send technical updates and promotional bulletins (with explicit opt-in consent)</li>
      </ul>

      <h2>3. Sharing of Information</h2>
      <p>We only share your personal data in strict compliance with applicable laws:</p>
      <ul>
        <li>With cold-chain logistics and air-cargo carriers for order fulfillment</li>
        <li>
          With payment service providers (<strong>iyzico</strong>) for PCI-DSS secure card
          processing
        </li>
        <li>With authorized regional sales desks and distribution partners</li>
        <li>With tax offices, veterinary directors, and legal authorities when mandated by law</li>
      </ul>
      <p className="font-bold text-navy">
        We NEVER sell, rent, or lease your personal data to third parties.
      </p>

      <h2>4. Data Security</h2>
      <ul>
        <li>256-Bit SSL encryption for all web transmission</li>
        <li>Strict administrative and technical measures against unauthorized access</li>
        <li>
          Online payment transactions are processed via{" "}
          <strong>PCI-DSS Level 1 certified iyzico infrastructure</strong>
        </li>
        <li>
          Data stored in compliance with <strong>KVKK (Law No. 6698)</strong> and GDPR retention
          standards
        </li>
      </ul>

      <h2>5. Cookies, Analytics and Advertising</h2>
      <p>
        We store only what the site needs to function — your language choice, session and cart —
        which requires no consent and cannot be declined while you use the site.
      </p>
      <p>
        <strong>Visitor statistics.</strong> We count visits on our own server at{" "}
        <strong>stats.varsco.com</strong>, using self-hosted software (Umami) that sets no cookie
        and stores nothing on your device. It records the page you opened, the site that linked you
        to it, the country your connection resolves to, and your browser and device type. Visits are
        grouped into a single session by a one-way code that is regenerated every day, so a visit
        yesterday cannot be matched to a visit today, and you cannot be recognised on any other
        website. This never receives your name, your email address, or anything you type into a
        form. Because nothing is stored on your device and none of it leaves our own systems, it
        runs for every visitor and is not one of the choices below.
      </p>
      <p>
        Beyond that there are <strong>two separate choices</strong>, and you can accept either
        without the other. Nothing in either category is collected before you choose, and declining
        is the default.
      </p>
      <p>
        <strong>Analytics.</strong> When enabled, we record the pages you visit, the language the
        site is displayed in, the country your connection resolves to, and a randomly generated
        identifier that lets us count one visitor's pages as a single visit. That identifier is not
        linked to your name or email, and we do not track you across other websites.
      </p>
      <p>
        <strong>Advertising.</strong> When enabled, Google and Meta may set identifiers in your
        browser so they can tell us which of our advertisements brought you here, and so that our
        advertisements can be shown to you on other websites and services. These identifiers are set
        by those companies under their own privacy policies, and this is the only category under
        which any information about you leaves this site for advertising purposes. Declining it
        stops all of it; the site works identically either way.
      </p>
      <p>
        <strong>If you arrive from an advertisement</strong>, the link you followed carries a click
        reference created by the advertising network. We keep that reference in your browser for the
        duration of your visit and attach it to an enquiry only if you choose to send us one, so
        that we can tell which advertisement produced a genuine enquiry. It is stored with that
        enquiry, kept for as long as we keep the enquiry itself, and is not used to build a profile
        of you.
      </p>
      <p>
        Your choices are stored in your browser, not in a cookie, so browser cookie settings do not
        control them. To change or withdraw them at any time, use the{" "}
        <strong>Cookie preferences</strong> link in the footer of any page. Withdrawing takes effect
        immediately and also deletes the identifiers described above, including those set for
        advertising.
      </p>
      <p>
        Analytics records are deleted automatically after 60 days of inactivity. If you send us an
        enquiry while analytics is enabled, the pages you viewed are attached to that enquiry and
        are then kept for as long as we keep the enquiry itself, as part of our record of your
        request.
      </p>

      <h2>6. Your Rights</h2>
      <p>Under KVKK and GDPR, you have the right to:</p>
      <ul>
        <li>Learn whether your personal data is processed and request access</li>
        <li>Request correction of inaccurate or incomplete personal data</li>
        <li>Request deletion or destruction of data (subject to statutory tax retention rules)</li>
        <li>Object to automated processing or withdraw marketing consent</li>
      </ul>
      <p>
        To exercise your rights, email our data controller at{" "}
        <a href="mailto:privacy@varsco.com" className="text-primary font-bold hover:underline">
          privacy@varsco.com
        </a>
        .
      </p>

      <h2>7. Updates</h2>
      <p>
        This policy may be updated periodically. The latest version will always be accessible on our
        portal with an updated effective date.
      </p>

      <div className="mt-8 rounded-2xl bg-surface-alt p-6 border border-border space-y-2 text-xs">
        <h3 className="font-display text-sm font-bold text-navy uppercase tracking-wider">
          Corporate Data Controller Details
        </h3>
        <p className="font-bold text-navy">{COMPANY.legalName}</p>
        <p>{COMPANY.address.full}</p>
        <p>Tax ID: 9240533729 | MERSIS: 0924053372900001</p>
        <p className="inline-flex flex-wrap items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Email:{" "}
          <a href="mailto:privacy@varsco.com" className="text-primary font-bold">
            privacy@varsco.com
          </a>{" "}
          | <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Tel: +90 232 290 57 56
        </p>
      </div>
    </LegalPage>
  );
}
